import React, { lazy, Suspense, ReactNode, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import LoginForm from './components/LoginForm';
import SessionManager from './components/SessionManager';
import ErrorBoundary from './components/ErrorBoundary';
import PerformanceDashboard from './components/PerformanceDashboard';
import { CustomerProvider } from './context/CustomerContext';
import { AuthProvider, useAuth } from './context/AuthContext';
import { AdminAuthProvider } from './context/AdminAuthContext';
import AdminRoute from './components/AdminRoute';
import AdminLayout from './components/AdminLayout';
import { ToastProvider } from './context/ToastContext';
import { ThemeProvider } from './context/ThemeContext';
import { ErrorProvider, useError } from './context/ErrorContext';
import { setupGlobalErrorHandlers } from './utils/globalErrorHandler';
import { ErrorNotificationHelper } from './utils/errorNotification';
import { initSentry } from './utils/sentryConfig';
import usePageTracking from './hooks/usePageTracking';

// Code splitting for better performance
const MainPage = lazy(() => import('./pages/MainPage'));
const CustomerManagementPage = lazy(() => import('./pages/CustomerManagementPage'));
const DepositPage = lazy(() => import('./pages/DepositPage'));
const PaymentPage = lazy(() => import('./pages/PaymentPage'));
const HistoryPage = lazy(() => import('./pages/HistoryPage'));
const StoreAdminPage = lazy(() => import('./pages/StoreAdminPage'));
const AdminLoginPage = lazy(() => import('./pages/AdminLoginPage'));
const AdminDashboardPage = lazy(() => import('./pages/AdminDashboardPage'));
const AdminStoresPage = lazy(() => import('./pages/AdminStoresPage'));
const AdminUsersPage = lazy(() => import('./pages/AdminUsersPage'));
const AdminReportsPage = lazy(() => import('./pages/AdminReportsPage'));
const SignupPage = lazy(() => import('./pages/SignupPage'));
const MailTestPage = lazy(() => import('./pages/MailTestPage'));

// Admin pages
const AdminNotFoundPage = lazy(() => import('./pages/admin/NotFoundPage'));
const StaffListPage = lazy(() => import('./pages/admin/StaffListPage'));
const StaffDetailPage = lazy(() => import('./pages/admin/StaffDetailPage'));
const AdminCustomerManagementPage = lazy(() => import('./pages/admin/CustomerManagementPage'));
const AdminSystemSettingsPage = lazy(() => import('./pages/admin/SystemSettingsPage'));
const AdminAuditLogsPage = lazy(() => import('./pages/admin/AuditLogsPage'));
const StoreDetailPage = lazy(() => import('./pages/admin/StoreDetailPage'));
const StoreEditPage = lazy(() => import('./pages/admin/StoreEditPage'));

// Error handler initialization component
const ErrorHandlerInitializer: React.FC = () => {
  const { showError } = useError();

  useEffect(() => {
    // グローバルエラーハンドラーを設定
    setupGlobalErrorHandlers();

    // ErrorNotificationHelperにErrorContextを登録
    ErrorNotificationHelper.setErrorContext((options) => {
      showError(options.message, options.severity, options);
    });

    // Sentry初期化（本番環境のみ）
    initSentry();
  }, [showError]);

  return null;
};

// Page tracking wrapper component
interface PageTrackingWrapperProps {
  children: ReactNode;
}

const PageTrackingWrapper: React.FC<PageTrackingWrapperProps> = ({ children }) => {
  usePageTracking();
  return <>{children}</>;
};

// Main App Routes component
const AppRoutes: React.FC = () => {
  const { isAuthenticated, loading, user } = useAuth();

  if (loading) {
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
          fontSize: '1.2rem',
          color: '#666'
        }}
      >
        読み込み中...
      </div>
    );
  }

  const LoadingFallback: React.FC = () => (
    <div style={{ padding: '20px', textAlign: 'center', color: '#666' }}>読み込み中...</div>
  );

  return (
    <PageTrackingWrapper>
      <div className="App">
        <Suspense fallback={<LoadingFallback />}>
          <Routes>
            {/* システム管理者用ルート */}
            <Route
              path="/admin/*"
              element={
                <AdminAuthProvider>
                  <Routes>
                    {/* デフォルトリダイレクト */}
                    <Route index element={<Navigate to="login" replace />} />

                    {/* 公開ルート */}
                    <Route path="login" element={<AdminLoginPage />} />

                    {/* 認証が必要なルート */}
                    <Route
                      element={
                        <AdminRoute>
                          <AdminLayout />
                        </AdminRoute>
                      }
                    >
                      <Route index element={<Navigate to="dashboard" replace />} />
                      <Route path="dashboard" element={<AdminDashboardPage />} />
                      <Route path="stores" element={<AdminStoresPage />} />
                      <Route path="stores/:storeId" element={<StoreDetailPage />} />
                      <Route path="stores/:storeId/edit" element={<StoreEditPage />} />
                      <Route path="users" element={<AdminUsersPage />} />
                      <Route path="staff" element={<StaffListPage />} />
                      <Route path="staff/:id" element={<StaffDetailPage />} />
                      <Route path="customers" element={<AdminCustomerManagementPage />} />
                      <Route path="settings" element={<AdminSystemSettingsPage />} />
                      <Route path="audit-logs" element={<AdminAuditLogsPage />} />
                      <Route path="reports" element={<AdminReportsPage />} />
                      <Route path="mail-test" element={<MailTestPage />} />
                    </Route>

                    {/* 404ページ */}
                    <Route path="/*" element={<AdminNotFoundPage />} />
                  </Routes>
                </AdminAuthProvider>
              }
            />

            {/* 通常ユーザー用ルート */}
            <Route
              path="/*"
              element={
                !isAuthenticated() ? (
                  <Routes>
                    <Route path="/login" element={<LoginForm />} />
                    <Route path="/signup" element={<SignupPage />} />
                    <Route path="*" element={<Navigate to="/login" replace />} />
                  </Routes>
                ) : (
                  <CustomerProvider>
                    <SessionManager />
                    <Routes>
                      <Route path="/" element={<MainPage />} />
                      <Route path="/customers" element={<CustomerManagementPage />} />
                      <Route path="/deposit" element={<DepositPage />} />
                      <Route path="/payment" element={<PaymentPage />} />
                      <Route path="/history" element={<HistoryPage />} />
                      <Route
                        path="/store-admin"
                        element={
                          user?.role === 'admin' ? <StoreAdminPage /> : <Navigate to="/" replace />
                        }
                      />
                      <Route path="*" element={<Navigate to="/" replace />} />
                    </Routes>
                  </CustomerProvider>
                )
              }
            />
          </Routes>
        </Suspense>
        {process.env.NODE_ENV === 'development' && <PerformanceDashboard />}
      </div>
    </PageTrackingWrapper>
  );
};

const App: React.FC = () => {
  return (
    <ErrorBoundary>
      <ThemeProvider>
        <ErrorProvider>
          <AuthProvider>
            <ToastProvider>
              <Router>
                <ErrorHandlerInitializer />
                <AppRoutes />
              </Router>
            </ToastProvider>
          </AuthProvider>
        </ErrorProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
};

export default App;
